/**
 * Pilihan tema terang, gelap, atau mengikuti sistem.
 *
 * Tiga keadaan, bukan dua. "Mengikuti sistem" adalah keadaan tersendiri: sekali
 * pengguna memilih terang secara eksplisit, laptop yang beralih gelap pada
 * malam hari tidak boleh ikut mengubah tampilannya — dan sebaliknya, pengguna
 * yang belum pernah memilih apa pun harus tetap mengikuti sistemnya.
 *
 * Pilihannya disimpan di `localStorage`. Ini satu-satunya hal yang memang layak
 * disimpan di sana: bukan rahasia, tidak bernilai bagi penyerang, dan justru
 * harus bertahan melewati muat-ulang. Token akses tetap hanya di memori — lihat
 * `api/client.ts`.
 */

export type ThemeChoice = 'sistem' | 'terang' | 'gelap'

export const THEME_KEY = 'osce-tema'

const CHOICES: readonly ThemeChoice[] = ['sistem', 'terang', 'gelap']

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && (CHOICES as readonly string[]).includes(value)
}

/**
 * Menafsirkan nilai yang tersimpan.
 *
 * Murni, karena inilah bagian yang layak diuji. `localStorage` dapat disunting
 * siapa pun yang membuka konsol peramban; nilai yang tidak dikenal harus jatuh
 * ke "sistem", bukan dipasang apa adanya sebagai atribut.
 */
export function resolveThemeChoice(stored: string | null): ThemeChoice {
  return isThemeChoice(stored) ? stored : 'sistem'
}

/**
 * Nilai atribut `data-theme` untuk satu pilihan, atau `null` bila tidak ada
 * yang perlu dinyatakan.
 *
 * `null` untuk "sistem" bukan kelalaian — justru itu yang membuatnya bekerja.
 * Tanpa atribut, CSS jatuh ke `prefers-color-scheme`. Memasang
 * `data-theme="system"` akan mematikan media query itu dan justru membekukan
 * tampilan pada satu tema.
 */
export function themeAttribute(choice: ThemeChoice): 'dark' | 'light' | null {
  if (choice === 'sistem') return null
  return choice === 'gelap' ? 'dark' : 'light'
}

export function readThemeChoice(): ThemeChoice {
  try {
    return resolveThemeChoice(localStorage.getItem(THEME_KEY))
  } catch {
    // Mode penyamaran sebagian peramban melempar saat localStorage disentuh.
    // Tema bukan hal yang layak menjatuhkan aplikasi.
    return 'sistem'
  }
}

/** Menuliskan pilihan ke elemen akar. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  const nilai = themeAttribute(choice)
  if (nilai === null) root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', nilai)
}

export function storeThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'sistem') localStorage.removeItem(THEME_KEY)
    else localStorage.setItem(THEME_KEY, choice)
  } catch {
    // Pilihan tetap berlaku untuk tab ini; hanya tidak bertahan.
  }
}
